"use client";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SelectionIndicator } from "@/components/ui/selection-indicator";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export interface FilterOption {
    value: string;
    label: string;
}

interface FilterSelectProps {
    value: string;
    onValueChange: ( value: string ) => void;
    options: FilterOption[];
    placeholder?: string;
    className?: string;
    renderValue?: ( value: string, option?: FilterOption ) => React.ReactNode;
}

export default function FilterSelect(
    { value, onValueChange, options, placeholder = "Select option", className, renderValue }: FilterSelectProps,
) {
    const [ open, setOpen ] = useState( false );
    const triggerRef = useRef<HTMLButtonElement>( null );
    const [ contentWidth, setContentWidth ] = useState<string>( "200px" );

    const selectedOption = options.find( ( option ) => option.value === value );
    const displayValue = renderValue ? renderValue( value, selectedOption ) : selectedOption?.label;
    const showPlaceholder = !value || !selectedOption
        || ( displayValue === null || displayValue === undefined || displayValue === "" );
    const buttonText = showPlaceholder ? placeholder : ( displayValue ?? placeholder );

    useEffect( () => {
        if ( open && triggerRef.current ) {
            const triggerWidth = triggerRef.current.offsetWidth;
            const extraSpace = 32;
            const calculatedWidth = Math.min( triggerWidth + extraSpace, 320 );
            setContentWidth( `${calculatedWidth}px` );
        }
    }, [ open ] );

    const handleSelect = ( selectedValue: string ) => {
        onValueChange( selectedValue );
        setOpen( false );
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    ref={triggerRef}
                    className={cn(
                        "flex h-11 w-full items-center justify-between gap-2 overflow-hidden rounded-[7.6px] border border-stroke px-3 py-px text-[14px] font-medium leading-normal text-t1 transition-colors focus:outline-none",
                        showPlaceholder && "text-t3",
                        className,
                    )}
                >
                    <span className="truncate">{buttonText}</span>
                    <ChevronDown className="size-4 shrink-0 text-t1" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                style={{ width: contentWidth }}
                className="p-1.5 rounded-md bg-brand-button border-0 shadow-[0px_0px_8px_0px_rgba(0,0,0,0.1),0px_8px_16px_0px_rgba(0,0,0,0.1)] max-w-[calc(100vw-32px)]"
                sideOffset={8}
            >
                <div className="flex flex-col max-h-[210px] overflow-y-auto scrollbar-hide">
                    {options.map( ( option ) => (
                        <button
                            key={option.value}
                            onClick={() => handleSelect( option.value )}
                            className={cn(
                                "flex items-center gap-3 px-2 py-3 rounded-md text-left transition-colors focus:outline-none",
                                "text-sm font-medium text-t1",
                            )}
                        >
                            <SelectionIndicator selected={value === option.value} className="shrink-0" />
                            <span className="truncate">{option.label}</span>
                        </button>
                    ) )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
